import { cn } from '@cibli/shared';
import { useMicVolume, useSpeakerVolume } from '@micdrop/react';
import { MicdropState, Speaker } from '@micdrop/web';
import { useEffect, useState } from 'react';

import DotsIcon from '../molecules/DotsIcon';
import InsigniaIcon from '../molecules/InsigniaIcon';
import PauseIcon from '../molecules/PauseIcon';

interface StatusCircleProps {
  size: number;
  isPlaying?: boolean;
  isLoading?: boolean;
  isCVGenerating?: boolean;
  centered?: boolean;
  micdropState?: MicdropState;
}

type StatusCircleStateClass = 'listening' | 'thinking' | 'paused' | 'talking' | 'default';

export default function StatusCircle({
  size,
  isPlaying = false,
  isLoading = false,
  isCVGenerating = false,
  centered = false,
  micdropState,
}: StatusCircleProps) {
  const { speakerVolume, maxSpeakerVolume } = useSpeakerVolume();
  const { micVolume, maxMicVolume } = useMicVolume();
  const shouldListenToSpeaker = isPlaying !== undefined;
  const [actualIsPlaying, setActualIsPlaying] = useState(
    shouldListenToSpeaker ? (isPlaying ?? false) : false,
  );

  useEffect(() => {
    if (shouldListenToSpeaker) {
      setActualIsPlaying(isPlaying || micdropState?.isAssistantSpeaking || Speaker.isPlaying);
    }
  }, [isPlaying, micdropState?.isAssistantSpeaking, shouldListenToSpeaker]);

  useEffect(() => {
    if (!shouldListenToSpeaker) {
      return;
    }

    const handleStartPlaying = () => {
      setActualIsPlaying(true);
    };

    const handleStopPlaying = () => {
      setActualIsPlaying(false);
    };

    Speaker.on('StartPlaying', handleStartPlaying);
    Speaker.on('StopPlaying', handleStopPlaying);

    setActualIsPlaying(Speaker.isPlaying || Boolean(micdropState?.isAssistantSpeaking));

    return () => {
      Speaker.off('StartPlaying', handleStartPlaying);
      Speaker.off('StopPlaying', handleStopPlaying);
    };
  }, [shouldListenToSpeaker, micdropState?.isAssistantSpeaking]);

  const effectiveState: Partial<MicdropState> = {
    ...(micdropState ?? {}),
    isAssistantSpeaking: Boolean(
      isPlaying !== undefined
        ? actualIsPlaying || micdropState?.isAssistantSpeaking
        : micdropState?.isAssistantSpeaking,
    ),
  };

  const sizeRatio = getSizeRatio({
    micdropState: effectiveState,
    micVolume,
    maxMicVolume,
    speakerVolume,
    maxSpeakerVolume,
    isLoading,
  });

  const circleSize = sizeRatio * size;

  const stateClass = getStateClass({ micdropState: effectiveState, isLoading, isCVGenerating });
  const shadowSize = (isSpeaking(stateClass, micdropState) ? 0.08 : 0.04) * size;
  const shadowSize2 = (isSpeaking(stateClass, micdropState) ? 0.16 : 0.08) * size;

  return (
    <div
      className={cn('flex items-center justify-center relative', centered && 'mx-auto')}
      style={{ width: size, height: size }}
    >
      <div
        className={cn(
          'status-circle rounded-full transition-all duration-200 ease-in-out flex items-center justify-center bg-primary',
          stateClass,
        )}
        style={{
          width: circleSize,
          height: circleSize,
          boxShadow: getBoxShadow(shadowSize, shadowSize2),
          border: stateClass === 'listening' ? `${0.03 * size}px solid var(--primary)` : undefined,
        }}
      >
        {getIcon(stateClass, size, sizeRatio)}
      </div>
    </div>
  );
}

function isSpeaking(stateClass: StatusCircleStateClass, micdropState?: Partial<MicdropState>) {
  if (!micdropState) {
    return ['talking', 'listening'].includes(stateClass);
  }

  return (
    ['talking', 'listening'].includes(stateClass) &&
    (micdropState?.isUserSpeaking || micdropState?.isAssistantSpeaking)
  );
}

function getStateClass(params: {
  micdropState?: Partial<MicdropState>;
  isLoading: boolean;
  isCVGenerating: boolean;
}): StatusCircleStateClass {
  const { micdropState, isLoading, isCVGenerating } = params;
  // Only show "thinking" (dots) when session is started; after stop() isProcessing can stay true (stale)
  const isActuallyProcessing = micdropState?.isStarted && micdropState?.isProcessing;
  if (isLoading || isActuallyProcessing || isCVGenerating) return 'thinking';
  if (micdropState?.isListening) return 'listening';
  if (micdropState?.isUserSpeaking) return 'listening';
  if (micdropState?.isAssistantSpeaking) return 'talking';
  if (micdropState?.isPaused) return 'paused';
  return 'default';
}

function getBoxShadow(shadowSize: number, shadowSize2: number): string {
  return `0px 0px 0px ${shadowSize}px rgba(36, 184, 148, 0.2), 0px 0px 0px ${shadowSize2}px rgba(36, 184, 148, 0.1)`;
}

function getIcon(stateClass: StatusCircleStateClass, size: number, sizeRatio: number) {
  const ratioIconSize = 0.3;

  if (stateClass === 'talking') {
    return (
      <InsigniaIcon
        width={size * ratioIconSize}
        height={size * ratioIconSize}
        sizeRatio={sizeRatio}
      />
    );
  }
  if (stateClass === 'listening' || stateClass === 'thinking') {
    return <DotsIcon width={size * ratioIconSize} height={size * ratioIconSize} />;
  }
  if (stateClass === 'paused') {
    return <PauseIcon width={size * ratioIconSize} height={size * ratioIconSize} />;
  }
  return null;
}

function getSizeRatio(params: {
  micVolume: number;
  maxMicVolume: number;
  speakerVolume: number;
  maxSpeakerVolume: number;
  isLoading: boolean;
  micdropState?: Partial<MicdropState>;
}) {
  const { micdropState, isLoading, micVolume, maxMicVolume, speakerVolume, maxSpeakerVolume } =
    params;
  if (isLoading) {
    return 0.5;
  }
  if (micdropState?.isPaused) {
    return 0.8;
  }
  if (micdropState?.isStarted && micdropState?.isProcessing) return 0.4;
  if (micdropState?.isListening) return 0.6;
  if (micdropState?.isUserSpeaking) {
    const denom = maxMicVolume + 100;
    const ratio =
      Number.isFinite(micVolume) && Number.isFinite(denom) && denom > 0
        ? Math.max(0, (micVolume + 100) / denom)
        : 0;
    return 0.4 + 0.6 * ratio;
  }
  if (micdropState?.isAssistantSpeaking) {
    const denom = maxSpeakerVolume + 100;
    const ratio =
      Number.isFinite(speakerVolume) && Number.isFinite(denom) && denom > 0
        ? Math.max(0, (speakerVolume + 100) / denom)
        : 0;
    return 0.4 + 0.6 * ratio;
  }
  return 0.5;
}
